import { useCallback, useEffect, useRef, useState } from 'react';
import { IconCheck, IconCrop, IconMaximize, IconX } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import DocumentCleanupCropPanel from './DocumentCleanupCropPanel';
import { useBodyScrollLock, useFitBox } from '../../components/image/useFullscreenStage';

/**
 * Crop popup, same shape as the NID / passport crop modals: full screen, the
 * page on a measured stage that it is fitted to (nothing scrolls, every corner
 * handle stays in view), and the actions pinned below.
 *
 * `imageSrc` is always the UNCROPPED page, and `initialQuad` is whatever crop is
 * currently applied — so reopening this shows the existing crop and lets it be
 * widened again, instead of cropping a crop.
 */
export default function DocumentCleanupCropModal({
    opened,
    onClose,
    imageSrc,
    initialQuad,
    fileName,
    onApply,
    busy = false,
}) {
    const { t } = useTranslation();
    const panelRef = useRef(null);
    const stageRef = useRef(null);
    const [, setReady] = useState(false);
    const fitBox = useFitBox(stageRef, opened);
    useBodyScrollLock(opened);

    useEffect(() => {
        if (!opened) setReady(false);
    }, [opened, imageSrc]);

    useEffect(() => {
        if (!opened) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape' && !busy) onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [opened, onClose, busy]);

    const handleApply = useCallback(() => {
        const panel = panelRef.current;
        if (!panel) return;
        try {
            onApply(panel.getQuad());
        } catch (e) {
            notifications.show({
                title: t('common.error'),
                message: e?.message === 'invalid quad'
                    ? t('documentCleanupImage.errors.invalidQuad')
                    : t('documentCleanupImage.errors.cropFailed'),
                color: 'red',
            });
        }
    }, [onApply, t]);

    if (!opened) return null;

    return (
        <div
            className="fixed inset-0 z-[200] flex h-dvh w-screen flex-col overflow-hidden bg-white dark:bg-zinc-900"
            role="dialog"
            aria-modal="true"
        >
            <div className="flex flex-none items-center justify-between gap-3 border-b border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                <div className="flex min-w-0 items-center gap-2">
                    <IconCrop size={18} className="shrink-0 text-amber-600 dark:text-amber-400" />
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-[#1C1815] dark:text-zinc-100">
                            {t('documentCleanupImage.cropTitle')}
                        </p>
                        {fileName ? (
                            <p className="truncate text-xs text-[#A8A099] dark:text-zinc-500">{fileName}</p>
                        ) : null}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    disabled={busy}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#ECE8E4] text-[#6B635C] transition-colors hover:border-primary/40 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-300"
                    aria-label={t('common.cancel')}
                >
                    <IconX size={16} stroke={2} />
                </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#1C1815] p-3 sm:p-5">
                <p className="mb-3 flex-none text-[13px] leading-relaxed text-white/75">
                    {t('documentCleanupImage.cropHint')}
                </p>
                <div ref={stageRef} className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden">
                    <DocumentCleanupCropPanel
                        ref={panelRef}
                        imageSrc={imageSrc}
                        initialQuad={initialQuad}
                        fitBox={fitBox}
                        onLoaded={() => setReady(true)}
                    />
                </div>
            </div>

            <div className="flex flex-none flex-wrap items-center justify-between gap-2 border-t border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                <button
                    type="button"
                    onClick={() => panelRef.current?.selectWholeImage()}
                    disabled={busy}
                    className="inline-flex h-11 items-center gap-1.5 rounded-xl border border-[#ECE8E4] bg-white px-4 text-[13px] font-semibold text-[#3D3833] transition-colors hover:border-amber-400 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                >
                    <IconMaximize size={16} stroke={1.9} />
                    {t('documentCleanupImage.selectWhole')}
                </button>

                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="inline-flex h-11 items-center gap-1.5 rounded-xl border border-[#ECE8E4] bg-white px-4 text-[13px] font-semibold text-[#3D3833] transition-colors hover:border-primary/40 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                    >
                        <IconX size={16} stroke={2} />
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={busy}
                        className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-primary px-5 text-[13px] font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
                    >
                        <IconCheck size={16} stroke={2} />
                        {t('documentCleanupImage.applyCrop')}
                    </button>
                </div>
            </div>
        </div>
    );
}
